import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Toolbox, CaretLeft, LinkSimple, QrCode, MagnifyingGlass, Copy, Trash, ChartBar } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const inputCls = "h-10 bg-transparent border-white/10 text-white text-sm placeholder:text-white/25";
const lbl = "text-[10px] tracking-[0.15em] uppercase text-white/50";

const UtmBuilder = ({ onSaved }) => {
  const [f, setF] = useState({ url: "", source: "", medium: "", campaign: "" });
  const [saving, setSaving] = useState(false);
  const [shortUrl, setShortUrl] = useState(null);
  const valid = f.url.startsWith("http") && f.source && f.medium && f.campaign;
  const link = valid
    ? `${f.url}${f.url.includes("?") ? "&" : "?"}utm_source=${encodeURIComponent(f.source)}&utm_medium=${encodeURIComponent(f.medium)}&utm_campaign=${encodeURIComponent(f.campaign)}`
    : "";

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/tools/links", { name: f.campaign, target_url: link, kind: "utm" });
      setShortUrl(data.short_url);
      toast.success("Link salvato: i clic verranno conteggiati nello storico");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="tool-utm-card">
      <LinkSimple size={32} weight="duotone" className="text-sky-400 mb-3" />
      <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Link UTM</h2>
      <p className="text-sm text-slate-400 mb-5">Crea link tracciati per misurare le campagne. Salvandoli ottieni un link corto con conteggio clic.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1 sm:col-span-2"><Label className={lbl}>URL di destinazione</Label>
          <Input data-testid="utm-url-input" value={f.url} onChange={(e) => { setF({ ...f, url: e.target.value }); setShortUrl(null); }} placeholder="https://www.michelchef.it/promo" className={inputCls} /></div>
        <div className="space-y-1"><Label className={lbl}>Sorgente (utm_source)</Label>
          <Input data-testid="utm-source-input" value={f.source} onChange={(e) => { setF({ ...f, source: e.target.value }); setShortUrl(null); }} placeholder="facebook, newsletter, google" className={inputCls} /></div>
        <div className="space-y-1"><Label className={lbl}>Mezzo (utm_medium)</Label>
          <Input data-testid="utm-medium-input" value={f.medium} onChange={(e) => { setF({ ...f, medium: e.target.value }); setShortUrl(null); }} placeholder="social, email, cpc" className={inputCls} /></div>
        <div className="space-y-1 sm:col-span-2"><Label className={lbl}>Campagna (utm_campaign)</Label>
          <Input data-testid="utm-campaign-input" value={f.campaign} onChange={(e) => { setF({ ...f, campaign: e.target.value }); setShortUrl(null); }} placeholder="promo-autunno-2026" className={inputCls} /></div>
      </div>
      {link && (
        <div className="mt-4 space-y-2">
          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 flex items-center gap-2">
            <code className="text-[11px] text-sky-300 break-all flex-1" data-testid="utm-result">{link}</code>
            <Button type="button" data-testid="utm-copy-button" variant="ghost" onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Link copiato"))}
              className="h-8 w-8 p-0 shrink-0 border border-white/15 text-white/60 hover:text-white rounded-full"><Copy size={13} /></Button>
          </div>
          {shortUrl ? (
            <div className="p-3 rounded-lg bg-emerald-400/5 border border-emerald-400/25 flex items-center gap-2" data-testid="utm-short-result">
              <code className="text-[11px] text-emerald-300 break-all flex-1">{shortUrl}</code>
              <Button type="button" data-testid="utm-short-copy-button" variant="ghost" onClick={() => navigator.clipboard.writeText(shortUrl).then(() => toast.success("Link corto copiato"))}
                className="h-8 w-8 p-0 shrink-0 border border-emerald-400/30 text-emerald-300/70 hover:text-emerald-300 rounded-full"><Copy size={13} /></Button>
            </div>
          ) : (
            <Button data-testid="utm-save-button" onClick={handleSave} disabled={saving}
              className="h-10 px-5 bg-white text-black hover:bg-gray-200 rounded-full text-xs font-semibold">
              {saving ? "Salvataggio..." : "Salva e traccia i clic"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const QrGenerator = ({ onSaved }) => {
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const [tracked, setTracked] = useState(null);
  const [loading, setLoading] = useState(false);
  const isUrl = text.trim().startsWith("http://") || text.trim().startsWith("https://");

  const generate = async () => {
    setLoading(true);
    setTracked(null);
    try {
      let qrData = text.trim();
      if (isUrl) {
        const { data } = await api.post("/admin/tools/links", { name: `QR ${new Date().toLocaleDateString("it-IT")}`, target_url: qrData, kind: "qr" });
        qrData = data.short_url;
        setTracked(data);
        onSaved();
      }
      const res = await api.get(`/admin/tools/qr?data=${encodeURIComponent(qrData)}`, { responseType: "blob" });
      setImg(URL.createObjectURL(res.data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generazione QR non riuscita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="tool-qr-card">
      <QrCode size={32} weight="duotone" className="text-emerald-400 mb-3" />
      <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Codice QR</h2>
      <p className="text-sm text-slate-400 mb-5">Genera un QR da stampare su volantini e menù. Se inserisci un link, le scansioni vengono conteggiate nello storico.</p>
      <div className="flex gap-2">
        <Input data-testid="qr-text-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="https://www.michelchef.it" className={inputCls} />
        <Button data-testid="qr-generate-button" onClick={generate} disabled={!text.trim() || loading}
          className="h-10 px-5 bg-white text-black hover:bg-gray-200 rounded-full text-xs font-semibold shrink-0 disabled:opacity-40">
          {loading ? "..." : "Genera"}
        </Button>
      </div>
      {img && (
        <div className="mt-4 flex items-center gap-4">
          <img src={img} alt="Codice QR" data-testid="qr-result-image" className="w-36 h-36 rounded-lg bg-white p-2" />
          <div className="space-y-1.5">
            <a href={img} download="qr-areapaq.png" data-testid="qr-download-link" className="block text-xs font-semibold text-emerald-300 hover:text-emerald-200">Scarica PNG →</a>
            {tracked && <p className="text-[11px] text-white/40" data-testid="qr-tracked-note">QR tracciato: ogni scansione conta come clic su <code className="text-emerald-300/80 break-all">{tracked.short_url}</code></p>}
          </div>
        </div>
      )}
    </div>
  );
};

const SerpPreview = () => {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [url, setUrl] = useState("");
  return (
    <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="tool-serp-card">
      <MagnifyingGlass size={32} weight="duotone" className="text-amber-400 mb-3" />
      <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Anteprima Google</h2>
      <p className="text-sm text-slate-400 mb-5">Scrivi titolo e descrizione di una pagina e verifica come apparirà nei risultati di ricerca.</p>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className={lbl}>Titolo <span className={title.length > 60 ? "text-red-400" : "text-white/30"}>({title.length}/60)</span></Label>
          <Input data-testid="serp-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Michel Chef — Cucina a domicilio a Milano" className={inputCls} />
        </div>
        <div className="space-y-1">
          <Label className={lbl}>Descrizione <span className={desc.length > 160 ? "text-red-400" : "text-white/30"}>({desc.length}/160)</span></Label>
          <Input data-testid="serp-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Menù personalizzati, ingredienti freschi..." className={inputCls} />
        </div>
        <div className="space-y-1">
          <Label className={lbl}>URL</Label>
          <Input data-testid="serp-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="www.michelchef.it › servizi" className={inputCls} />
        </div>
      </div>
      {(title || desc) && (
        <div className="mt-5 p-4 rounded-lg bg-white" data-testid="serp-preview">
          <p className="text-[13px] text-[#202124] truncate">{url || "www.esempio.it"}</p>
          <p className="text-[19px] leading-snug text-[#1a0dab] truncate">{title || "Titolo della pagina"}</p>
          <p className="text-[13px] text-[#4d5156] line-clamp-2">{desc || "La descrizione apparirà qui..."}</p>
        </div>
      )}
    </div>
  );
};

const TrackedLinksHistory = ({ links, onDelete }) => (
  <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="tracked-links-card">
    <ChartBar size={32} weight="duotone" className="text-fuchsia-400 mb-3" />
    <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Storico link tracciati</h2>
    <p className="text-sm text-slate-400 mb-5">Link UTM salvati e QR generati, con il conteggio di clic e scansioni.</p>
    {links.length === 0 ? (
      <p className="text-sm text-white/40" data-testid="tracked-links-empty">Nessun link tracciato ancora: salva un link UTM o genera un QR con un URL.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="tracked-links-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-white/30 text-left">
              <th className="py-2 pr-3 font-medium">Nome</th>
              <th className="py-2 pr-3 font-medium">Tipo</th>
              <th className="py-2 pr-3 font-medium">Link corto</th>
              <th className="py-2 pr-3 font-medium text-right">Clic</th>
              <th className="py-2 pr-3 font-medium">Ultimo clic</th>
              <th className="py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.id} className="border-t border-white/5 text-white/70" data-testid={`tracked-link-row-${l.id}`}>
                <td className="py-2 pr-3 truncate max-w-[160px]" title={l.target_url}>{l.name || "—"}</td>
                <td className="py-2 pr-3">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${l.kind === "qr" ? "text-emerald-400" : "text-sky-400"}`}>{l.kind === "qr" ? "QR" : "UTM"}</span>
                </td>
                <td className="py-2 pr-3">
                  <button type="button" onClick={() => navigator.clipboard.writeText(l.short_url).then(() => toast.success("Link copiato"))}
                    className="inline-flex items-center gap-1 text-sky-300/80 hover:text-sky-300" title={l.short_url} data-testid={`tracked-link-copy-${l.id}`}>
                    <Copy size={11} /> /t/{l.slug}
                  </button>
                </td>
                <td className="py-2 pr-3 text-right text-white font-semibold" data-testid={`tracked-link-clicks-${l.id}`}>{l.clicks}</td>
                <td className="py-2 pr-3 text-white/40">{l.last_click_at ? new Date(l.last_click_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                <td className="py-2 text-right">
                  <button type="button" onClick={() => onDelete(l)} className="text-red-400/50 hover:text-red-400" data-testid={`tracked-link-delete-${l.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default function MarketingToolsPage() {
  const [links, setLinks] = useState([]);
  const loadLinks = () => api.get("/admin/tools/links").then(({ data }) => setLinks(data)).catch(() => {});
  useEffect(() => { loadLinks(); }, []);

  const handleDelete = async (l) => {
    try {
      await api.delete(`/admin/tools/links/${l.id}`);
      toast.success("Link eliminato");
      loadLinks();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-tools-page">
      <div className="mb-10 animate-fade-up">
        <Link to="/marketing" data-testid="back-to-marketing" className="inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors mb-4">
          <CaretLeft size={13} /> Marketing
        </Link>
        <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
          <Toolbox size={13} className="text-emerald-400" /> Strumenti di marketing
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Strumenti</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl">Utility rapide per le tue campagne: link tracciati con conteggio clic, codici QR con scansioni conteggiate e anteprima dei risultati Google.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UtmBuilder onSaved={loadLinks} />
        <QrGenerator onSaved={loadLinks} />
        <div className="lg:col-span-2"><TrackedLinksHistory links={links} onDelete={handleDelete} /></div>
        <div className="lg:col-span-2"><SerpPreview /></div>
      </div>
    </main>
  );
}
